import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  IconButton,
  Select,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { CONSTANTS as Q_CONSTANTS } from 'src/components/questionnaire/Constants';
import { resolveVitalStatus } from '../pedigree-utils';
import ClinicalRecordsPanel from './ClinicalRecordsPanel';

const inputStyle = {
  border: '1px solid #ccc',
  borderRadius: 4,
  padding: '4px 6px',
  fontSize: 13,
  width: '100%',
  height: 32,
  boxSizing: 'border-box',
  fontFamily: 'inherit'
};

const STATUS_OPTIONS = [
  { value: 'true', label: 'Alive' },
  { value: 'false', label: 'Deceased' }
];

// Short gender labels for the compact table cell
const GENDER_SHORT = {
  Male: 'Ma',
  Female: 'Fe',
  Indeterminate: 'In',
  'Male - AFAB': 'AFAB',
  'Male - UAAB': 'UAAB',
  'Female - AMAB': 'AMAB',
  'Non-binary/gender diverse': 'NB',
  'Non-binary/gender diverse - AFAB': 'NB-A',
  'Non-binary/gender diverse - UAAB': 'NB-U',
  'Unknown/Not Stated Unknown': 'Unk'
};

function DateInput({ value, onChange }) {
  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => !value && e.target.showPicker?.()}
      style={{
        ...inputStyle,
        width: 118,
        color: value ? 'inherit' : 'transparent'
      }}
    />
  );
}

function CellForColumn({ col, member, entry, actions, ethnicityOptions }) {
  switch (col.id) {
    case 'firstName':
      return (
        <input
          type="text"
          value={
            entry.firstName !== null ? entry.firstName : member.firstName || ''
          }
          placeholder="First name"
          onChange={(e) =>
            actions.updatePersonField(member.pid, 'firstName', e.target.value)
          }
          style={inputStyle}
        />
      );
    case 'lastName':
      return (
        <input
          type="text"
          value={
            entry.lastName !== null ? entry.lastName : member.lastName || ''
          }
          placeholder="Last name"
          onChange={(e) =>
            actions.updatePersonField(member.pid, 'lastName', e.target.value)
          }
          style={inputStyle}
        />
      );
    case 'gender': {
      const genderValue =
        entry.genderIdentity !== null
          ? entry.genderIdentity
          : member.genderIdentity ??
            (member.gender
              ? member.gender[0].toUpperCase() + member.gender.slice(1)
              : 'Unknown');
      return (
        <Select
          size="small"
          variant="outlined"
          value={genderValue}
          onChange={(e) =>
            actions.updatePersonField(
              member.pid,
              'genderIdentity',
              e.target.value
            )
          }
          sx={{ fontSize: 13, height: 32, minWidth: 90 }}
          renderValue={(v) => GENDER_SHORT[v] ?? v}
        >
          {Q_CONSTANTS.genderIdentityOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      );
    }
    case 'status': {
      const effectiveDod = entry.dod !== null ? entry.dod : member.dod || '';
      const statusValue =
        entry.isAlive !== null
          ? entry.isAlive
          : resolveVitalStatus({ isAlive: member.isAlive, dod: effectiveDod });
      return (
        <Select
          size="small"
          variant="outlined"
          value={statusValue}
          onChange={(e) =>
            actions.updatePersonFields(member.pid, {
              isAlive: e.target.value,
              ...(e.target.value === 'true' && effectiveDod ? { dod: '' } : {})
            })
          }
          sx={{ fontSize: 13, height: 32, minWidth: 90 }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      );
    }
    case 'dob': {
      const dobValue = entry.dob !== null ? entry.dob : member.dob || '';
      return (
        <DateInput
          value={dobValue}
          onChange={(v) => actions.updatePersonField(member.pid, 'dob', v)}
        />
      );
    }
    case 'dod': {
      const dodValue = entry.dod !== null ? entry.dod : member.dod || '';
      return (
        <DateInput
          value={dodValue}
          onChange={(v) =>
            actions.updatePersonFields(
              member.pid,
              v ? { dod: v, isAlive: 'false' } : { dod: v }
            )
          }
        />
      );
    }
    case 'ethnicity': {
      const ethValue =
        entry.ethnicity !== null ? entry.ethnicity : member.ethnicity || [];
      return (
        <Autocomplete
          multiple
          size="small"
          options={ethnicityOptions}
          value={ethValue}
          onChange={(_, val) =>
            actions.updatePersonField(member.pid, 'ethnicity', val)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={ethValue.length === 0 ? 'Ethnicity' : ''}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  sx: { fontSize: 12, py: '1px !important' }
                }
              }}
            />
          )}
          renderValue={(tags) => (
            <Typography variant="caption" noWrap>
              {tags.join(', ')}
            </Typography>
          )}
          sx={{ minWidth: 160 }}
        />
      );
    }
    case 'mobile':
      return (
        <input
          type="text"
          value={entry.mobile !== null ? entry.mobile : member.mobile || ''}
          placeholder="Mobile"
          onChange={(e) =>
            actions.updatePersonField(member.pid, 'mobile', e.target.value)
          }
          style={inputStyle}
        />
      );
    case 'email':
      return (
        <input
          type="email"
          value={entry.email !== null ? entry.email : member.email || ''}
          placeholder="Email"
          onChange={(e) =>
            actions.updatePersonField(member.pid, 'email', e.target.value)
          }
          style={inputStyle}
        />
      );
    case 'hospitalNumber':
      return (
        <input
          type="text"
          value={
            entry.hospitalNumber !== null
              ? entry.hospitalNumber
              : member.hospitalNumber || ''
          }
          placeholder="Hospital Number"
          onChange={(e) =>
            actions.updatePersonField(
              member.pid,
              'hospitalNumber',
              e.target.value
            )
          }
          style={inputStyle}
        />
      );
    case 'healthcareId':
      return (
        <input
          type="text"
          value={
            entry.healthcareId !== null
              ? entry.healthcareId
              : member.healthcareId || ''
          }
          placeholder="Healthcare ID"
          onChange={(e) =>
            actions.updatePersonField(
              member.pid,
              'healthcareId',
              e.target.value
            )
          }
          style={inputStyle}
        />
      );
    case 'isHide': {
      const hideValue =
        entry.isHide !== null ? entry.isHide : Boolean(member.isHide);
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Checkbox
            size="small"
            checked={Boolean(hideValue)}
            onChange={(e) =>
              actions.updatePersonField(member.pid, 'isHide', e.target.checked)
            }
          />
        </Box>
      );
    }
    case 'recordsReviewed': {
      const reviewedValue =
        entry.recordsReviewed !== null
          ? entry.recordsReviewed
          : Boolean(member.recordsReviewed);
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Checkbox
            size="small"
            checked={Boolean(reviewedValue)}
            onChange={(e) =>
              actions.updatePersonField(
                member.pid,
                'recordsReviewed',
                e.target.checked
              )
            }
          />
        </Box>
      );
    }
    default:
      return null;
  }
}

export default function MemberRow({
  member,
  index,
  entry,
  isDirty,
  actions,
  familyId,
  visibleColumns,
  ethnicityOptions,
  colSpan
}) {
  const [expanded, setExpanded] = useState(false);

  const displayId = member.upn ?? index + 1;

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { bgcolor: 'action.hover' },
          borderLeft: isDirty ? '3px solid #BA7517' : '3px solid transparent'
        }}
      >
        {/* Expand toggle */}
        <TableCell padding="none" sx={{ width: 40, pl: 0.5 }}>
          <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? (
              <RemoveIcon sx={{ fontSize: 16 }} />
            ) : (
              <AddIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </TableCell>

        {/* UPN */}
        <TableCell sx={{ width: 60, py: 0.75 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600
            }}
          >
            {displayId}
          </Typography>
        </TableCell>

        {/* Visible data columns */}
        {visibleColumns.map((col) => (
          <TableCell
            key={col.id}
            sx={{ py: 0.5, width: col.width, overflow: 'hidden' }}
          >
            <CellForColumn
              col={col}
              member={member}
              entry={entry}
              actions={actions}
              ethnicityOptions={ethnicityOptions}
            />
          </TableCell>
        ))}

        {/* Dirty indicator */}
        <TableCell sx={{ py: 0.5, width: 40, pr: 1 }}>
          {isDirty && (
            <FiberManualRecordIcon sx={{ fontSize: 10, color: '#BA7517' }} />
          )}
        </TableCell>
      </TableRow>
      {/* Expanded clinical records panel */}
      {expanded && (
        <TableRow>
          <TableCell colSpan={colSpan} sx={{ p: 0, borderBottom: 'none' }}>
            <ClinicalRecordsPanel
              member={member}
              entry={entry}
              actions={actions}
              familyId={familyId}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
